import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Grid,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
} from '@mui/material';
import { ArrowLeft, Search, Plus, Pencil, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import { appointmentFlowApi } from '../../../api';
import useUIStore from '../../../store/useUIStore';
import useConfirm from '../../../hooks/useConfirm';

const DAYS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

const emptyForm = {
  name: '',
  email: '',
  phone: '',
  serviceIds: [],
  workingDays: [1, 2, 3, 4, 5],
  startTime: '',
  endTime: '',
  status: 'active',
};

const statusLabel = (value) => (value === 1 || value === 'active' ? 'active' : 'inactive');

const EmployeesPage = () => {
  const { showError } = useUIStore();
  const { confirm } = useConfirm();
  const [employees, setEmployees] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const rowsPerPage = 10;

  useEffect(() => {
    fetchEmployees();
    fetchServices();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, employees]);

  const fetchEmployees = async () => {
    try {
      const response = await appointmentFlowApi.getEmployees();
      const data = response?.data?.data || response?.data || [];
      setEmployees(Array.isArray(data) ? data : []);
    } catch (error) {
      setEmployees([]);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await appointmentFlowApi.getServices();
      const data = response?.data?.data || response?.data || [];
      setAllServices(Array.isArray(data) ? data : []);
    } catch (error) {
      setAllServices([]);
    }
  };

  const serviceLookup = useMemo(
    () => new Map(allServices.map((service) => [String(service.id), service.name])),
    [allServices]
  );

  const availableServices = useMemo(() => allServices, [allServices]);

  const applyFilters = () => {
    if (!searchTerm) {
      setFilteredData(employees);
      setCurrentPage(1);
      return;
    }

    const search = searchTerm.toLowerCase();
    const filtered = employees.filter((item) =>
      (item.name || '').toLowerCase().includes(search) ||
      (item.email || '').toLowerCase().includes(search) ||
      (item.phone || '').toLowerCase().includes(search)
    );
    setFilteredData(filtered);
    setCurrentPage(1);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      serviceIds: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleWorkingDayToggle = (day) => {
    setFormData((prev) => {
      const exists = prev.workingDays.includes(day);
      return {
        ...prev,
        workingDays: exists
          ? prev.workingDays.filter((d) => d !== day)
          : [...prev.workingDays, day].sort(),
      };
    });
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    try {
      await appointmentFlowApi.createEmployee({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        status: formData.status,
        workingDays: formData.workingDays,
        startTime: formData.startTime,
        endTime: formData.endTime,
        serviceIds: formData.serviceIds,
      });
      setShowAddModal(false);
      resetForm();
      fetchEmployees();
    } catch (error) {
      showError(error?.response?.data?.message || 'Failed to add employee');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await appointmentFlowApi.updateEmployee(formData.id, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        status: formData.status,
        workingDays: formData.workingDays,
        startTime: formData.startTime,
        endTime: formData.endTime,
        serviceIds: formData.serviceIds,
      });
      setShowEditModal(false);
      resetForm();
      fetchEmployees();
    } catch (error) {
      showError(error?.response?.data?.message || 'Failed to update employee');
    }
  };

  const handleEdit = (employee) => {
    setFormData({
      id: employee.id,
      name: employee.name || '',
      email: employee.email || '',
      phone: employee.phone || '',
      serviceIds: Array.isArray(employee.serviceIds) ? employee.serviceIds : [],
      workingDays: Array.isArray(employee.workingDays) ? employee.workingDays : [],
      startTime: employee.startTime || '',
      endTime: employee.endTime || '',
      status: statusLabel(employee.status),
    });
    setShowEditModal(true);
  };

  const handleDelete = async (employeeId) => {
    const isConfirmed = await confirm({
      title: 'Delete Employee',
      message: 'Are you sure you want to delete this employee?',
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'error',
    });
    if (!isConfirmed) return;

    try {
      await appointmentFlowApi.deleteEmployee(employeeId);
      fetchEmployees();
    } catch (error) {
      showError(error?.response?.data?.message || 'Failed to delete employee');
    }
  };

  const handleStatusToggle = async (employeeId, currentStatus) => {
    const nextStatus = statusLabel(currentStatus) === 'active' ? 'inactive' : 'active';
    try {
      await appointmentFlowApi.updateEmployee(employeeId, { status: nextStatus });
      fetchEmployees();
    } catch (error) {
      showError(error?.response?.data?.message || 'Failed to update status');
    }
  };

  const resetForm = () => {
    setFormData(emptyForm);
  };

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentData = filteredData.slice(startIndex, startIndex + rowsPerPage);

  const renderServiceNames = (serviceIds) => {
    if (!Array.isArray(serviceIds) || !serviceIds.length) return '-';
    return serviceIds
      .map((id) => serviceLookup.get(String(id)))
      .filter(Boolean)
      .join(', ');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <Button variant="text" startIcon={<ArrowLeft />} href="/appointments" sx={{ minWidth: 0, px: 0 }}>
          Back
        </Button>
        <Typography variant="h5">Employees Management</Typography>
      </Stack>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center">
            <Search size={16} />
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Stack>
        </CardContent>
      </Card>

      <Stack direction="row" justifyContent="flex-start" mb={2}>
        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          onClick={() => {
            resetForm();
            setShowAddModal(true);
          }}
        >
          Add New Employee
        </Button>
      </Stack>

      <Card>
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>S.No</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Services</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No employees found
                    </TableCell>
                  </TableRow>
                ) : (
                  currentData.map((employee, index) => (
                    <TableRow key={employee.id} hover>
                      <TableCell>{startIndex + index + 1}</TableCell>
                      <TableCell>{employee.name || '-'}</TableCell>
                      <TableCell>{employee.email || '-'}</TableCell>
                      <TableCell>{employee.phone || '-'}</TableCell>
                      <TableCell>{renderServiceNames(employee.serviceIds)}</TableCell>
                      <TableCell>
                        <Chip
                          label={statusLabel(employee.status)}
                          size="small"
                          sx={{
                            bgcolor: statusLabel(employee.status) === 'active' ? 'success.light' : 'error.light',
                            color: statusLabel(employee.status) === 'active' ? 'success.contrastText' : 'error.contrastText',
                            textTransform: 'capitalize',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Pencil size={14} />}
                            onClick={() => handleEdit(employee)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<Trash2 size={14} />}
                            onClick={() => handleDelete(employee.id)}
                          >
                            Delete
                          </Button>
                          {statusLabel(employee.status) === 'active' ? (
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<ToggleRight size={16} />}
                              onClick={() => handleStatusToggle(employee.id, employee.status)}
                            >
                              Active
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              variant="contained"
                              color="inherit"
                              startIcon={<ToggleLeft size={16} />}
                              onClick={() => handleStatusToggle(employee.id, employee.status)}
                            >
                              Inactive
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} mt={3}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Typography variant="body2">
                Page {currentPage} of {totalPages}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Dialog open={showAddModal} onClose={() => setShowAddModal(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6">Add New Employee</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Assign services, availability, and schedule.
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Employee Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Name"
                      name="name"
                      fullWidth
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Email"
                      name="email"
                      type="email"
                      fullWidth
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Phone"
                      name="phone"
                      fullWidth
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Services</InputLabel>
                      <Select
                        multiple
                        name="serviceIds"
                        label="Services"
                        value={formData.serviceIds}
                        onChange={handleServiceChange}
                        renderValue={(selected) => renderServiceNames(selected)}
                      >
                        {availableServices.map((service) => (
                          <MenuItem key={service.id} value={service.id}>
                            <Checkbox checked={formData.serviceIds.includes(service.id)} />
                            {service.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select name="status" label="Status" value={formData.status} onChange={handleInputChange}>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Schedule
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Working Days
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      {DAYS.map((day) => (
                        <Stack key={day.value} direction="row" spacing={0.5} alignItems="center">
                          <Checkbox
                            size="small"
                            checked={formData.workingDays.includes(day.value)}
                            onChange={() => handleWorkingDayToggle(day.value)}
                          />
                          <Typography variant="body2">{day.label}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Start Time"
                      name="startTime"
                      type="time"
                      fullWidth
                      value={formData.startTime}
                      onChange={handleInputChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="End Time"
                      name="endTime"
                      type="time"
                      fullWidth
                      value={formData.endTime}
                      onChange={handleInputChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddModal(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddSubmit}>
            Add Employee
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showEditModal} onClose={() => setShowEditModal(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6">Edit Employee</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Update services, availability, and schedule.
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Employee Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Name"
                      name="name"
                      fullWidth
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Email"
                      name="email"
                      type="email"
                      fullWidth
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Phone"
                      name="phone"
                      fullWidth
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Services</InputLabel>
                      <Select
                        multiple
                        name="serviceIds"
                        label="Services"
                        value={formData.serviceIds}
                        onChange={handleServiceChange}
                        renderValue={(selected) => renderServiceNames(selected)}
                      >
                        {availableServices.map((service) => (
                          <MenuItem key={service.id} value={service.id}>
                            <Checkbox checked={formData.serviceIds.includes(service.id)} />
                            {service.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select name="status" label="Status" value={formData.status} onChange={handleInputChange}>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Schedule
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Working Days
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      {DAYS.map((day) => (
                        <Stack key={day.value} direction="row" spacing={0.5} alignItems="center">
                          <Checkbox
                            size="small"
                            checked={formData.workingDays.includes(day.value)}
                            onChange={() => handleWorkingDayToggle(day.value)}
                          />
                          <Typography variant="body2">{day.label}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Start Time"
                      name="startTime"
                      type="time"
                      fullWidth
                      value={formData.startTime}
                      onChange={handleInputChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="End Time"
                      name="endTime"
                      type="time"
                      fullWidth
                      value={formData.endTime}
                      onChange={handleInputChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditModal(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSubmit}>
            Update Employee
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeesPage;
